import { AnimatePresence, MotiView } from "moti";
import { useCallback } from "react";
import { usePathname } from "solito/navigation";
import { useRouter } from "solito/router";
import { useShallow } from "zustand/react/shallow";
import { ArrowRight as ArrowRightIcon } from "@tamagui/lucide-icons";
import { backgroundPrimaryHex, StyledNextActionButton } from "@medbillai/ui";
import { loginUrl } from "@medbillai/utils";
import { analyticsService } from "../../../lib/analytics";
import { spring } from "../../../lib/animations/motiConstants";
import { useSignUpOnboardingStore } from "../../../lib/sign-up-onboarding/signUpOnboardingStore";
import { stages } from "./getStartedConfig";
export const GetStartedNextButton = () => {
  const {
    push
  } = useRouter();
  const pathname = usePathname();
  // We use a shallow selector to avoid re-rendering the button when the
  // state changes, use shallow compares the output of the selector with the
  // previous output.
  const {
    next,
    canAdvance,
    isLastStage,
    setIsSignUpOnboarding,
    step,
    isSignUpOnboarding
  } = useSignUpOnboardingStore(useShallow(s => {
    const stage = stages[s.step]!; // static array → stable ref
    const hasSelections = (s.answers[stage.id] ?? []).length > 0;
    const showNextButton = !stage.autoContinue && (!stage.choices?.options || hasSelections);
    return {
      next: s.next,
      // stable function
      canAdvance: showNextButton || s.isSignUpOnboarding,
      // if autoContinue is defined, we don't want to show the button
      isLastStage: s.step === stages.length - 1,
      setIsSignUpOnboarding: s.setIsSignUpOnboarding,
      // stable function
      step: s.step,
      isSignUpOnboarding: s.isSignUpOnboarding
    };
  }));
  const handlePress = useCallback(() => {
    // Never called because we don't render the button if canAdvance is false
    if (!canAdvance) {
      console.error("[GetStartedNextButton] canAdvance is false but user attempted to navigate to the next stage");
      return;
    }

    // Track the current stage selections before advancing
    const currentStage = stages[step];
    if (currentStage?.choices?.options) {
      const currentSelections = useSignUpOnboardingStore.getState().answers[currentStage.id] ?? [];

      // Track stage-specific completion events
      if (currentStage.id === "help" && currentSelections.length > 0) {
        analyticsService.trackEvent("SignUpOnboardingUserFeaturesCompleted", {
          selections: currentSelections,
          isNewOnboarding: true
        });
      } else if (currentStage.id === "who" && currentSelections.length > 0) {
        analyticsService.trackEvent("SignUpOnboardingUsageScopeCompleted", {
          selections: currentSelections,
          isNewOnboarding: true
        });
      }
    }
    if (isLastStage) {
      analyticsService.trackEvent("OnboardingFlowStepForward", {
        from: pathname,
        to: loginUrl(),
        isNewOnboarding: true
      });
      setIsSignUpOnboarding(true);
      push(loginUrl());
    } else {
      // Track advancing to next stage
      const nextStage = stages[step + 1];
      analyticsService.trackEvent("OnboardingFlowStepForward", {
        from: pathname,
        to: nextStage?.id ?? "unknown",
        isNewOnboarding: true
      });
      next();
    }
  }, [canAdvance, next, push, isLastStage, setIsSignUpOnboarding, pathname, step]);
  return <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-component="GetStartedNextButton" data-sentry-source-file="GetStartedNextButton.tsx">
      {/**
       * Is SignUpOnboarding is true, we want to show the button to go to the login page,
       * this is to handle the case where a user navigates back to the get started screen
       * after they have already completed the onboarding and we do not want them to be
       * stuck due to the auto routing.
       */}
      {canAdvance || isSignUpOnboarding ? <MotiView style={{
      position: "absolute",
      bottom: 48,
      right: 16,
      zIndex: 1000
    }} key="get-started-next-button" from={{
      opacity: 0,
      translateY: 20
    }} animate={{
      opacity: 1,
      translateY: 0
    }} exit={{
      opacity: 0,
      translateY: 20
    }} transition={{
      ...spring
    }}>
          <StyledNextActionButton onPress={handlePress}>
            <ArrowRightIcon height={24} width={24} color={backgroundPrimaryHex} />
          </StyledNextActionButton>
        </MotiView> : null}
    </AnimatePresence>;
};